/**
 * 群組資料載入 Hook
 * 處理群組成員和子群組的載入邏輯
 */

import { useState, useCallback, useRef } from 'react';
import { fetchGroupData } from '../services/groupService';

/**
 * 處理功能性群組和子群組的合併邏輯
 */
const processGroupData = (subGroups_data, functionalGroup_data, deptId) => {
  // 過濾掉父群組本身，以及非deptId開頭的群組
  functionalGroup_data = functionalGroup_data.filter(f => {
    const code = f.displayName.split(".")[1];
    return code && code.includes(deptId) && code !== deptId;
  });

  let functionalGroup_data_filtered;
  if (subGroups_data.length === 0) {
    // 這層（deptId）下的所有直屬functional group，不重複
    functionalGroup_data_filtered = functionalGroup_data.filter(f => {
      const code = f.displayName.split(".")[1];
      if (!code.startsWith(deptId) || code === deptId) return false;
      // 檢查有沒有更短且一樣是deptId開頭的群組，且是它的 prefix
      return !functionalGroup_data.some(other => {
        const otherCode = other.displayName.split(".")[1];
        return otherCode.length < code.length &&
          otherCode.startsWith(deptId) &&
          code.startsWith(otherCode);
      });
    });
  } else {
    // 若subGroups_data不為空，維持原有合併去重的方式
    functionalGroup_data_filtered = functionalGroup_data.filter(f =>
      !subGroups_data.some(s => {
        const fCode = f.displayName.split(".")[1];
        const sCode = s.displayName.split(".")[1];
        return fCode && sCode && fCode.includes(sCode);
      }) &&
      f.displayName.split(".")[1].includes(deptId)
    );
  }

  let combinedGroups_data = [...functionalGroup_data_filtered, ...subGroups_data];

  // 使用部門編號排序
  combinedGroups_data.sort((a, b) => {
    const aCode = a.displayName.split(".")[1]?.replace(/[a-zA-Z]/g, "") || "";
    const bCode = b.displayName.split(".")[1]?.replace(/[a-zA-Z]/g, "") || "";
    return aCode.localeCompare(bCode);
  });

  // 過濾掉以 P 結尾的群組
  combinedGroups_data = combinedGroups_data.filter(c => {
    const code = c.displayName.split(".")[1];
    return code && !code.toLowerCase().endsWith("p");
  });

  // 為每個子群組添加 children 屬性
  return combinedGroups_data.map(g => ({
    ...g,
    code: g.displayName.split(/[.\s]+/)[g.displayName.split(/[.\s]+/).length - 1],
    children: []
  }));
};

export const useGroupData = (getAccessToken, tokenRef) => {
  const [groupMembers, setGroupMembers] = useState({});
  const [groupSubgroups, setGroupSubgroups] = useState({});
  const [loadingGroups, setLoadingGroups] = useState(new Set());
  
  // 使用 ref 來追蹤已載入的節點，避免重複載入
  const loadedNodesRef = useRef(new Set());

  /**
   * 獲取群組的成員和子群組
   * @param {string} groupId - Azure 群組 ID
   * @param {string} deptId - 部門 ID
   * @param {string} nodePath - 節點路徑（用於區分不同層級）
   */
  const fetchMembers = useCallback(async (groupId, deptId, nodePath) => {
    // 如果已經載入過，就不需要再載入
    if (loadedNodesRef.current.has(nodePath)) {
      return;
    }

    setLoadingGroups(prev => new Set(prev).add(nodePath));
    
    try {
      const token = getAccessToken ? await getAccessToken() : await Office.auth.getAccessToken();
      if (!token) {
        setLoadingGroups(prev => {
          const next = new Set(prev);
          next.delete(nodePath);
          return next;
        });
        return;
      }

      // 使用服務層獲取資料
      const { subgroups, members, functionalGroups } = await fetchGroupData(
        groupId,
        deptId,
        getAccessToken,
        tokenRef
      );

      // 處理子群組（合併功能性群組和子群組）
      if (subgroups || functionalGroups) {
        const processedSubgroups = processGroupData(
          subgroups || [],
          functionalGroups || [],
          deptId
        );
        setGroupSubgroups(prev => ({ ...prev, [nodePath]: processedSubgroups }));
      }

      // 處理成員
      if (members) {
        setGroupMembers(prev => ({ ...prev, [nodePath]: members }));
      }

      // 標記為已載入
      loadedNodesRef.current.add(nodePath);
    } catch (error) {
      console.error("Error fetching members and subgroups:", error);
    } finally {
      setLoadingGroups(prev => {
        const next = new Set(prev);
        next.delete(nodePath);
        return next;
      });
    }
  }, [getAccessToken, tokenRef]);

  return {
    groupMembers,
    groupSubgroups,
    loadingGroups,
    fetchMembers,
  };
};
